import { NgIf } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS } from '@app/constants';
import { DialogService, FeedbackService, LoadingService } from '@app/core/services';
import { PaymentCardTypeEnum } from '@app/enums/system-settings/payment-card-type.enum';
import { BaseServerFilterEntity } from '@app/models';
import { PaymentCardModel } from '@app/models/system-settings/payment-card.model';
import { ErrorSummaryComponent } from '@app/shared/components/error-summary/error-summary.component';
import { FormMode } from '@app/shared/forms/components';
import { FormComponent } from '@app/shared/forms/components/form.component';
import { PurchaseCardSetupService } from '../services/purchase-card-setup.service';
import { PurchaseCardImportSetupComponent } from './import-setup/import-setup.component';

@Component({
	selector: 'purchase-card-setup',
	templateUrl: './purchase-card-setup.component.html',
	styleUrls: [],
	standalone: true,
	imports: [NgIf, MatButtonModule, ErrorSummaryComponent, FormComponent, PurchaseCardImportSetupComponent],
})
export class PurchaseCardSetupComponent implements OnInit {
	@ViewChild(PurchaseCardImportSetupComponent) formComponent: PurchaseCardImportSetupComponent;

	errors: string[] = [];
	entity: PaymentCardModel = {
		Id: 0,
		TypeId: PaymentCardTypeEnum.Pcard,
	};

	originalEntity: PaymentCardModel;

	@Input() isReadOnly: boolean;

	fields: FormlyFieldConfig[] = [];

	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: false,
			flyout: false,
			selects: {
				sites: [],
			},
		},
	};

	public form = new UntypedFormGroup({});

	constructor(
		private readonly loadingService: LoadingService,
		private readonly purchaseCardSetupService: PurchaseCardSetupService,
		protected readonly dialogService: DialogService,
		protected readonly feedbackService: FeedbackService
	) {}

	ngOnInit(): void {
		this.purchaseCardSetupService.getSites().subscribe((result) => {
			if (result.sites) {
				this.options.formState.selects.sites = result.sites.map((site) => ({
					value: site.Id,
					label: `${site.SiteCode} - ${site.SiteName}`,
				}));
			}
		});

		this.purchaseCardSetupService.getCardByType(PaymentCardTypeEnum.Pcard).subscribe((result) => {
			if (result.isValid && result.data) {
				this.entity = this.createPaymentCardModel(result.data);
			}
			this.originalEntity = cloneDeep(this.entity);

			this.setFields();
		});

		if (this.isReadOnly) {
			this.options.formState.mode = FormMode.View;
		}
	}

	public CanDeactivate(): boolean {
		return !this.form.dirty;
	}

	cancelChanges(): void {
		this.dialogService
			.open({
				title: 'Unsaved Changes',
				message: 'There are unsaved changes.  Are you sure you want to cancel?',
				okText: 'Yes',
				cancelText: 'No',
			})
			.subscribe((result) => {
				if (result) {
					this.entity = cloneDeep(this.originalEntity);
					this.setFields();
					this.form.markAsPristine();
				}
			});
	}

	saveChanges(): void {
		this.errors = [];
		this.entity.VendorId = this.entity.VendorFilterEntity.Value;
		this.purchaseCardSetupService.createOrUpdate(this.entity).subscribe((result) => {
			if (result.isValid) {
				this.entity = this.createPaymentCardModel(result.data);
				this.feedbackService.success('updated', 'P-Card');
				this.form.markAsPristine();
				this.originalEntity = cloneDeep(this.entity);
			} else {
				this.errors = result.errors;
			}
		});
	}

	openSetup(): void {
		this.formComponent.edit(this.entity.Id);
	}

	private setFields(): void {
		this.fields = [
			{
				fieldGroup: [
					{
						key: 'ProviderName',
						type: FORMLY_FIELD_TYPES.input,
						templateOptions: {
							label: 'Provider Name',
							required: true,
							maxLength: 25,
						},
						validators: {
							validation: [FORMLY_FIELD_VALIDATORS.requiredNoWhitespace],
						},
					},
				],
			},
			{
				fieldGroup: [
					{
						key: 'VendorFilterEntity',
						type: FORMLY_FIELD_TYPES.autocomplete,
						templateOptions: {
							label: 'Vendor',
							required: true,
							filter: (value: string | any): Observable<any> => {
								this.loadingService.skipNextRequest();
								return this.filterAutocomplete(value);
							},
							filterDisplayValueFunc: (value: BaseServerFilterEntity): any => value.Label,
							filterValueFunc: (value: BaseServerFilterEntity): any => value.Value,
							dropCustomValues: true,
							maxLength: 50,
							selectedItem: this.entity.VendorFilterEntity?.Label,
						},
					},
				],
			},
			{
				fieldGroup: [
					{
						key: 'NextInvoiceNumber',
						type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
						templateOptions: {
							label: 'Next Invoice Number',
							required: true,
							min: 1,
							max: 2147483647,
						},
					},
				],
			},
			{
				fieldGroup: [
					{
						key: 'DefaultSiteId',
						type: FORMLY_FIELD_TYPES.select,
						templateOptions: {
							label: 'Default Site',
							required: true,
						},
						expressionProperties: {
							'templateOptions.options': 'formState.selects.sites',
						},
					},
				],
			},
		];
	}

	private filterAutocomplete(value: string): Observable<any> {
		let filterValue = this.entity.VendorFilterEntity?.Label;
		if (!filterValue) {
			filterValue = value;
		}
		return this.purchaseCardSetupService.searchVendorsForFilter(filterValue).pipe(
			map((data) =>
				data.map((item) => {
					const label = `${item.ShortName} - ${item.VendorName}`;
					return new BaseServerFilterEntity(item.Id, label, 'vendorId');
				})
			)
		);
	}

	private createPaymentCardModel(pcard: PaymentCardModel) {
		const label = `${pcard.Vendor.ShortName} - ${pcard.Vendor.VendorName}`;
		const model = {
			Id: pcard.Id,
			Vendor: pcard.Vendor,
			VendorId: pcard.VendorId,
			VendorFilterEntity: new BaseServerFilterEntity(pcard.Vendor.Id, label, 'vendorId'),
			DefaultSiteId: pcard.DefaultSiteId,
			TypeId: pcard.TypeId,
			ProviderName: pcard.ProviderName,
			NextInvoiceNumber: pcard.NextInvoiceNumber,
		} as PaymentCardModel;

		return model;
	}
}
